import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Loader2, Plus, Snowflake, UserCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, Td, Th } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { Modal } from '@/shared/ui/Modal'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { useElectionContext } from '@/shared/election/context'
import { NoElectionSelected } from '@/shared/election/ElectionSelector'
import { EntityPicker } from '@/shared/pickers/EntityPicker'
import { UnitPicker } from '@/features/candidacy/UnitPicker'
import {
  closeVoting, createNomination, freezeRoll, listNominations, openRoll, openVoting,
  type Nomination,
} from './api'
import { NominationStatusChip, SEATS, levelForSeat, methodLabel, seatLabel } from './vocab'
import { AssistedVoteModal } from './AssistedVoteModal'
import { useCan } from '@/shared/auth/session'
import { DateTimePicker } from '@/components/erp/date-picker'

/**
 * Contests: set one up, freeze its roll, run it.
 *
 * ## Freezing is the moment that matters
 *
 * Before it, the electorate is whatever the membership register happens to say. After it,
 * the roll is fixed and a membership created later simply is not on it. That is the entire
 * anti-stacking control, and it is why the button warns rather than just acting: freezing
 * cannot be undone, and a roll frozen too early excludes real members.
 */
export function ContestsPage() {
  const { electionId } = useElectionContext()
  const [params, setParams] = useSearchParams()
  const [rows, setRows] = useState<Nomination[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [assisting, setAssisting] = useState<Nomination | null>(null)
  const creating = params.get('new') === '1'
  const mayAssist = useCan('nomination:assist-vote')

  const load = useCallback(async () => {
    if (!electionId) { setRows([]); return }
    try {
      setError(null)
      setRows(await listNominations({ electionId, size: 300 }))
    } catch (err) {
      setError(explain(err))
      setRows([])
    }
  }, [electionId])

  useEffect(() => { void load() }, [load])

  if (!electionId) return <NoElectionSelected what="A nomination contest" />

  const act = async (id: string, what: 'open-roll' | 'freeze' | 'open' | 'close') => {
    if (what === 'freeze' && !window.confirm(
      'Freezing fixes who may vote in this contest. Members who join afterwards will NOT be '
      + 'on the roll, and this cannot be undone. Continue?')) return
    setBusyId(id)
    setError(null)
    try {
      if (what === 'open-roll') await openRoll(id)
      else if (what === 'freeze') await freezeRoll(id)
      else if (what === 'open') await openVoting(id)
      else await closeVoting(id)
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-erp-base font-semibold">Contests</h2>
          <p className="text-erp-xs text-muted-foreground mt-0.5">
            One party, one seat, one place. Several can run at once — that is the normal case.
          </p>
        </div>
        <button type="button"
          onClick={() => setParams((p) => { p.set('new', '1'); return p }, { replace: true })}
          className="erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2">
          <Plus className="h-3.5 w-3.5" /> New contest
        </button>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />

      {rows !== null && rows.length === 0 && !error && (
        <EmptyState title="No contests yet"
          hint="A contest is one party's primary for one seat at one place. It needs members
                on the register before its roll can be frozen." />
      )}

      {(rows === null || rows.length > 0) && (
        <ErpTable head={(
          <>
            <Th>Contest</Th>
            <Th>Party</Th>
            <Th>Status</Th>
            <Th className="text-right">Roll</Th>
            <Th className="text-right">Voted</Th>
            <Th className="text-right">Turnout</Th>
            <Th>Closes</Th>
            <Th />
          </>
        )}>
          {rows === null ? <LoadingRows colSpan={8} />
            : rows.map((n) => (
              <tr key={n.id}>
                <Td>
                  <span className="font-medium">{n.title}</span>
                  <span className="block text-erp-xs text-muted-foreground">
                    {seatLabel(n.seatTypeCode)} · {n.unitName ?? '—'} · {methodLabel(n.methodCode)}
                  </span>
                </Td>
                <Td>{n.partyShortName ?? n.partyName ?? '—'}</Td>
                <Td><NominationStatusChip code={n.statusCode} /></Td>
                <Td className="text-right tabular-nums">
                  {n.rollFrozenAt ? fmtNumber(n.eligibleVoters)
                    : <span className="text-muted-foreground">not frozen</span>}
                </Td>
                <Td className="text-right tabular-nums">{fmtNumber(n.votesCast)}</Td>
                <Td className="text-right tabular-nums">
                  {n.turnoutPct == null ? '—' : `${n.turnoutPct}%`}
                </Td>
                <Td className="text-erp-xs text-muted-foreground">{fmtDateTime(n.closesAt)}</Td>
                <Td>
                  <div className="flex items-center justify-end gap-2">
                    {busyId === n.id && <Loader2 className="h-3 w-3 animate-spin" />}
                    {n.statusCode === 'DRAFT' && (
                      <Action label="Open roll" onClick={() => void act(n.id, 'open-roll')} />
                    )}
                    {n.statusCode === 'ROLL_OPEN' && (
                      <button type="button" onClick={() => void act(n.id, 'freeze')}
                        className="inline-flex items-center gap-1 text-erp-xs text-chocolate
                                   hover:underline">
                        <Snowflake className="h-3 w-3" /> Freeze roll
                      </button>
                    )}
                    {n.statusCode === 'ROLL_FROZEN' && (
                      <Action label="Open voting" onClick={() => void act(n.id, 'open')} />
                    )}
                    {n.statusCode === 'VOTING' && mayAssist && n.allowAssisted && (
                      <button type="button" onClick={() => setAssisting(n)}
                        className="inline-flex items-center gap-1 text-erp-xs text-chocolate
                                   hover:underline">
                        <UserCheck className="h-3 w-3" /> Record a vote
                      </button>
                    )}
                    {n.statusCode === 'VOTING' && (
                      <Action label="Close voting" onClick={() => void act(n.id, 'close')} />
                    )}
                  </div>
                </Td>
              </tr>
            ))}
        </ErpTable>
      )}

      <Note tone="info">
        While a contest is open, <strong>Record a vote</strong> casts for a member in person —
        for anyone with no phone on record. It needs <code>nomination:assist-vote</code>, and
        your account is recorded against the vote while the ballot itself stays anonymous.
      </Note>

      <Note tone="info">
        Turnout here is of the <strong>frozen roll</strong> — the members eligible for this
        contest — not of the party's whole membership and not of the voter register. The three
        are very different denominators.
      </Note>

      <AssistedVoteModal open={assisting !== null} contest={assisting}
        onClose={() => setAssisting(null)} onDone={load} />

      <CreateModal open={creating} electionId={electionId}
        onClose={() => setParams((p) => { p.delete('new'); return p }, { replace: true })}
        onDone={load} />
    </div>
  )
}

function Action({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className="text-erp-xs text-chocolate hover:underline">{label}</button>
  )
}

// ── create ───────────────────────────────────────────────────────────────────────────────

function CreateModal({ open, electionId, onClose, onDone }: {
  open: boolean; electionId: string; onClose: () => void; onDone: () => void | Promise<void>
}) {
  const [partyId, setPartyId] = useState<string | null>(null)
  const [seatType, setSeatType] = useState('MCA')
  const [unitId, setUnitId] = useState<string | null>(null)
  const [title, setTitle] = useState('')
  const [method, setMethod] = useState('UNIVERSAL_MEMBER')
  const [rollCutoffAt, setRollCutoffAt] = useState('')
  const [opensAt, setOpensAt] = useState('')
  const [closesAt, setClosesAt] = useState('')
  const [threshold, setThreshold] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) return
    setPartyId(null); setSeatType('MCA'); setUnitId(null); setTitle('')
    setMethod('UNIVERSAL_MEMBER'); setRollCutoffAt(''); setOpensAt(''); setClosesAt('')
    setThreshold(''); setError(null); setBusy(false)
  }, [open])

  const ready = partyId && unitId && title.trim() && rollCutoffAt && opensAt && closesAt

  const submit = async () => {
    if (!ready) return
    setBusy(true)
    try {
      setError(null)
      await createNomination({
        electionId, partyId: partyId!, seatTypeCode: seatType, unitId: unitId!,
        title: title.trim(), methodCode: method,
        rollCutoffAt: new Date(rollCutoffAt).toISOString(),
        opensAt: new Date(opensAt).toISOString(),
        closesAt: new Date(closesAt).toISOString(),
        winThreshold: threshold ? Number(threshold) : null,
      })
      await onDone()
      onClose()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="New nomination contest" width="lg">
      <div className="space-y-3">
        <ErrorStrip message={error} className="rounded-md border" />
        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">Party</span>
            <EntityPicker source="party" value={partyId} onChange={setPartyId} />
          </label>
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">Seat</span>
            <select value={seatType} className="erp-input h-9 w-full text-erp-sm"
              onChange={(e) => { setSeatType(e.target.value); setUnitId(null) }}>
              {SEATS.map((s) => <option key={s} value={s}>{seatLabel(s)}</option>)}
            </select>
          </label>
          <div>
            <span className="block text-erp-xs text-muted-foreground mb-1">Where</span>
            {/* Narrowed to the level the SEAT is contested at — the server's SeatTypeRules,
                surfaced as a filter rather than as a rejection after the fact. */}
            <UnitPicker level={levelForSeat(seatType)} value={unitId} onPick={setUnitId} />
          </div>
        </div>

        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">Title</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200}
            className="erp-input h-9 w-full text-erp-sm"
            placeholder="e.g. Kangemi Ward MCA Primary" />
        </label>

        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">
              Membership cutoff
            </span>
            <DateTimePicker
              value={rollCutoffAt}
              onChange={setRollCutoffAt}
              className="h-9 w-full text-erp-sm"
            />
          </label>
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">Voting opens</span>
            <DateTimePicker
              value={opensAt}
              onChange={setOpensAt}
              className="h-9 w-full text-erp-sm"
            />
          </label>
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">Voting closes</span>
            <DateTimePicker
              value={closesAt}
              onChange={setClosesAt}
              className="h-9 w-full text-erp-sm"
            />
          </label>
        </div>

        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">Method</span>
            <select value={method} onChange={(e) => setMethod(e.target.value)}
              className="erp-input h-9 w-full text-erp-sm">
              {['UNIVERSAL_MEMBER', 'DELEGATE', 'CONSENSUS', 'DIRECT_TICKET'].map((m) => (
                <option key={m} value={m}>{methodLabel(m)}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">
              Win threshold % (blank = plurality)
            </span>
            <input inputMode="decimal" value={threshold}
              onChange={(e) => setThreshold(e.target.value.replace(/[^0-9.]/g, ''))}
              className="erp-input h-9 w-full text-erp-sm" placeholder="50" />
          </label>
        </div>

        <Note>
          The membership cutoff must be on or before voting opens. Anyone who joins the party
          after it will not be on this contest's roll — which is exactly what stops a faction
          registering members the night before.
        </Note>

        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="erp-btn h-9 px-3 text-erp-sm">
            Cancel
          </button>
          <button type="button" disabled={busy || !ready} onClick={() => void submit()}
            className={cn('erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2',
              (busy || !ready) && 'opacity-50 cursor-not-allowed')}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Create
          </button>
        </div>
      </div>
    </Modal>
  )
}
